import { AlertCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import type { FinalReviewData } from '../types/contract-checker.types';

interface ReviewSummaryProps {
  data: FinalReviewData;
  /** Where the "Back" button at the top of the header should point. */
  backHref: string;
}

/**
 * Top-of-page header for the Final Review:
 *   - back button (ghost + arrow, matching the "new" page for consistency)
 *   - "Final Step" eyebrow + clipboard icon
 *   - h1 title ("Final Review: {projectName}")
 *   - summary card with point count + risk chips
 *
 * Pure presentational — no state, no callbacks.
 */
export function ReviewSummary({ data, backHref }: ReviewSummaryProps) {
  const { t } = useTranslation();

  return (
    <header className="mb-5 sm:mb-7">
      <div className="mb-3">
        <Button asChild variant="ghost" size="sm" className="gap-2 text-globe-grey">
          <Link to={backHref}>
            <ArrowLeft className="h-4 w-4" />
            {t('ORGCONTRACTCHECKER_RESULT_BACK', { defaultValue: 'Back to analyses' })}
          </Link>
        </Button>
      </div>

      <h1 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 font-heading line-clamp-2">
        {t('ORGCONTRACTCHECKER_RESULT_TITLE', {
          defaultValue: 'Final Review: {{name}}',
          name: data.projectName,
        })}
      </h1>

      <div className="bg-card border border-border rounded-xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-5 shadow-[0_4px_20px_rgba(0,31,53,0.08)]">
        <div className="min-w-0">
          <p className="text-base sm:text-lg md:text-xl font-bold font-nunito">
            {t('ORGCONTRACTCHECKER_RESULT_POINTS', {
              defaultValue: '{{count}} Points to Review',
              count: data.totalPoints,
            })}
          </p>
          <p className="text-xs sm:text-sm text-globe-grey mt-1 font-nunito">
            {t('ORGCONTRACTCHECKER_RESULT_SUBTITLE', {
              defaultValue: 'Ensure all counter-clauses align with your strategic risk tolerance.',
            })}
          </p>
        </div>

        <div className="flex gap-2 sm:gap-3 flex-wrap shrink-0">
          <RiskCountChip
            tone="high"
            icon={<AlertTriangle className="w-4 h-4 text-destructive shrink-0" />}
            label={t('ORGCONTRACTCHECKER_RESULT_HIGH', {
              defaultValue: '{{count}} High Risk',
              count: data.highRiskCount,
            })}
          />
          <RiskCountChip
            tone="medium"
            icon={<AlertCircle className="w-4 h-4 text-[hsl(var(--warning))] shrink-0" />}
            label={t('ORGCONTRACTCHECKER_RESULT_MEDIUM', {
              defaultValue: '{{count}} Medium',
              count: data.mediumRiskCount,
            })}
          />
        </div>
      </div>
    </header>
  );
}

interface RiskCountChipProps {
  tone: 'high' | 'medium';
  icon: React.ReactNode;
  label: string;
}

/** Pill summarising one risk-bucket's count (e.g. "5 High Risk"). */
function RiskCountChip({ tone, icon, label }: RiskCountChipProps) {
  return (
    <div
      className={
        tone === 'high'
          ? 'flex items-center gap-2 px-2.5 sm:px-3 py-1.5 bg-destructive/10 rounded-lg'
          : 'flex items-center gap-2 px-2.5 sm:px-3 py-1.5 bg-[hsl(var(--warning))/0.15] rounded-lg'
      }
    >
      {icon}
      <span
        className={
          tone === 'high'
            ? 'font-bold text-destructive text-xs sm:text-sm whitespace-nowrap'
            : 'font-bold text-[hsl(var(--warning))] text-xs sm:text-sm whitespace-nowrap'
        }
      >
        {label}
      </span>
    </div>
  );
}

export default ReviewSummary;
